/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The walk-in (lead-less) create-reservation screen: vehicle + customer entry
 * (plate looks the vehicle up), the intake card (arrival / business type /
 * mileage / advisor / customer type), the booked items (picked from the
 * service-subject catalog), and a remark — then the create CTA.
 */

import { Typography } from '@euler/app/components/typography';
import { RemarkField } from '@euler/app/flows/leads/pages/create-reservation/components/RemarkField';
import { SaveButton } from '@euler/app/flows/leads/pages/create-reservation/components/SaveButton';
import { Card } from '@euler/app/flows/leads/pages/create-reservation/components/common/Card';
import { Eyebrow } from '@euler/app/flows/leads/pages/create-reservation/components/common/Eyebrow';
import { FieldRow } from '@euler/app/flows/leads/pages/create-reservation/components/common/FieldRow';
import { SelectPill } from '@euler/app/flows/leads/pages/create-reservation/components/common/SelectPill';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { OrderTagList } from '@euler/app/flows/order/components/OrderTagList';
import { useCreateReservationForm } from '@euler/app/flows/reservations/pages/create/functions/useCreateReservationForm';
import { ReservationItemsPanel } from '@euler/app/flows/reservations/pages/detail/components/ReservationItemsPanel';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import { CalendarClock, ChevronRight } from 'lucide-react-native';
import { ScrollView, TextInput, TouchableOpacity, View } from 'react-native';

export const CreateReservationScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();
    const form = useCreateReservationForm();
    const [datePickerVisible, showDatePicker, hideDatePicker] =
      useDateTimePicker();

    // `alignSelf` is what actually sizes these: FieldRow's value cell is a
    // COLUMN with `alignItems: 'flex-end'`, so a child's width comes from its
    // content and `flex: 1` only grows its height. Left to measure itself, a
    // native TextInput ends up narrower than its own text — clipped on the left
    // while focused, scrolled back to the first character once blurred. Stretch
    // it to the cell instead; `textAlign` keeps the value right-aligned.
    // (`flex: 1` still earns its keep in the mileage row, which lays its input
    // out in a row of its own.)
    const inputStyle = {
      flex: 1,
      alignSelf: 'stretch',
      fontSize: rpx(15),
      color: LD.ink,
      padding: 0,
      textAlign: 'right',
    } as const;

    // Match the picker rows: a placeholder renders regular, a typed value bold.
    const fontFor = (filled: boolean) =>
      filled ? FontFamily.NotoSans.Bold : FontFamily.NotoSans.Regular;

    return (
      <View style={{ flex: 1, backgroundColor: LD.canvas }}>
        <ScrollView
          contentContainerStyle={{ padding: rpx(14), paddingBottom: rpx(36) }}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={
              isDesktop
                ? { width: '100%', maxWidth: rpx(560), alignSelf: 'center' }
                : undefined
            }
          >
            <Eyebrow first required>
              车辆 / 客户
            </Eyebrow>
            <Card>
              <FieldRow
                label="车牌号"
                required
                first
                labelAccessory={
                  <TouchableOpacity
                    onPress={form.onOpenPlateKeyboard}
                    hitSlop={{ top: 8, bottom: 8, left: 4, right: 8 }}
                    style={{ marginLeft: rpx(8) }}
                  >
                    <Typography.Text size={rpx(12)} color={LD.brand} medium>
                      车牌键盘
                    </Typography.Text>
                  </TouchableOpacity>
                }
              >
                <TextInput
                  value={form.licensePlateNo}
                  onChangeText={form.onChangeLicensePlate}
                  onBlur={form.onLookupPlate}
                  placeholder="输入车牌号"
                  placeholderTextColor={LD.mist}
                  autoCapitalize="characters"
                  style={[
                    inputStyle,
                    { fontFamily: fontFor(!!form.licensePlateNo) },
                  ]}
                />
              </FieldRow>
              <FieldRow label="车辆" onPress={form.onPickVehicle}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Typography.Text
                    size={rpx(15)}
                    color={form.vehicleLabel ? LD.ink : LD.mist}
                    bold={form.vehicleLabel ? true : undefined}
                    numberOfLines={1}
                  >
                    {form.lookingUp
                      ? '识别中…'
                      : form.vehicleLabel || '未识别 · 点击选择'}
                  </Typography.Text>
                  <ChevronRight
                    size={rpx(16)}
                    color={LD.mist}
                    style={{ marginLeft: rpx(2) }}
                  />
                </View>
              </FieldRow>
              <FieldRow label="客户名称">
                <TextInput
                  value={form.customerName}
                  onChangeText={form.setCustomerName}
                  placeholder="填写客户名称"
                  placeholderTextColor={LD.mist}
                  style={[
                    inputStyle,
                    { fontFamily: fontFor(!!form.customerName) },
                  ]}
                />
              </FieldRow>
              <FieldRow label="联系电话">
                <TextInput
                  value={form.customerPhone}
                  onChangeText={form.setCustomerPhone}
                  placeholder="填写联系电话"
                  placeholderTextColor={LD.mist}
                  keyboardType="phone-pad"
                  style={[
                    inputStyle,
                    { fontFamily: fontFor(!!form.customerPhone) },
                  ]}
                />
              </FieldRow>
            </Card>

            <Eyebrow required>预约信息</Eyebrow>
            <Card>
              <FieldRow
                label="预计到店时间"
                required
                first
                onPress={showDatePicker}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <CalendarClock
                    size={rpx(16)}
                    color={form.arrival ? LD.brand : LD.mist}
                  />
                  <Typography.Text
                    size={rpx(15)}
                    color={form.arrival ? LD.ink : LD.mist}
                    bold={form.arrival ? true : undefined}
                    style={{ marginLeft: rpx(6) }}
                  >
                    {form.arrival ? format(form.arrival, 'yyyy-MM-dd') : '选择'}
                  </Typography.Text>
                </View>
              </FieldRow>
              <FieldRow label="业务类型" required alignTop>
                <OrderTagList
                  tags={form.tags}
                  onAddTag={form.addTag}
                  onTagRemoved={form.removeTag}
                  title="选择业务类型"
                  placeholder="选择业务类型"
                  style={{ width: '100%', justifyContent: 'flex-end' }}
                />
              </FieldRow>
              <FieldRow label="预计里程">
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <TextInput
                    value={form.mileage}
                    onChangeText={t =>
                      form.setMileage(t.replace(/[^0-9]/g, ''))
                    }
                    keyboardType="number-pad"
                    placeholder="—"
                    placeholderTextColor={LD.mist}
                    style={[
                      inputStyle,
                      { fontFamily: fontFor(!!form.mileage) },
                    ]}
                  />
                  <Typography.Text
                    size={rpx(13)}
                    color={LD.mist}
                    style={{ marginLeft: rpx(6) }}
                  >
                    公里
                  </Typography.Text>
                </View>
              </FieldRow>
              <FieldRow label="服务顾问" required onPress={form.onPickAgent}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Typography.Text
                    size={rpx(15)}
                    color={form.serviceAgent ? LD.ink : LD.mist}
                    bold={form.serviceAgent ? true : undefined}
                  >
                    {form.serviceAgent?.name ?? '选择'}
                  </Typography.Text>
                  <ChevronRight
                    size={rpx(16)}
                    color={LD.mist}
                    style={{ marginLeft: rpx(2) }}
                  />
                </View>
              </FieldRow>
              <FieldRow label="客户类型" alignTop>
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    gap: rpx(8),
                  }}
                >
                  {form.customerTypes.map(ct => (
                    <SelectPill
                      key={ct.type}
                      label={ct.shortName}
                      active={form.customerType === ct.type}
                      onPress={() => form.toggleCustomerType(ct.type)}
                    />
                  ))}
                </View>
              </FieldRow>
            </Card>

            <ReservationItemsPanel
              items={form.items}
              onAdd={form.onEditItems}
            />

            <Eyebrow>备注</Eyebrow>
            <RemarkField value={form.remark} onChangeText={form.setRemark} />

            <SaveButton onPress={form.onSubmit} />
          </View>
        </ScrollView>

        <DateTimePicker
          isVisible={datePickerVisible}
          mode="date"
          date={form.arrival ?? addDays(new Date(), 1)}
          onConfirm={date => {
            form.setArrival(date);
            hideDatePicker();
          }}
          onCancel={hideDatePicker}
          confirmTextIOS="确认"
          cancelTextIOS="关闭"
        />
      </View>
    );
  },
  {
    title: '新建预约',
  },
);
